import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

function BlockchainNetwork() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const nodeCount = 60;
    const nodes = Array.from({ length: nodeCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.3,
      vy: (Math.random() - 0.5) * 0.3,
    }));

    const hexPath = (ctx, x, y, r) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 6;
        const px = x + r * Math.cos(angle);
        const py = y + r * Math.sin(angle);
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
    };

    let animId;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > canvas.width) n.vx *= -1;
        if (n.y < 0 || n.y > canvas.height) n.vy *= -1;
      });

      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 140) {
            const alpha = (1 - dist / 140) * 0.35;
            ctx.strokeStyle = `rgba(160, 100, 240, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach((n) => {
        hexPath(ctx, n.x, n.y, 7);
        ctx.fillStyle = "#7c3aed";
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: "100%", height: "100%", display: "block" }}
    />
  );
}

export default function HeroSection() {
  return (
    <div
      style={{
        background: "#f8f8fc",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top hero: headline + description */}
      <section
        style={{
          position: "relative",
          zIndex: 1,
          padding: "clamp(48px, 8vw, 80px) clamp(20px, 5vw, 64px) clamp(24px, 4vw, 40px)",
          maxWidth: 1280,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(2rem, 6vw, 5rem)",
            fontWeight: 600,
            lineHeight: 1.1,
            color: "#0f0f1a",
            margin: "0 0 clamp(16px, 3vw, 32px)",
            letterSpacing: "-0.02em",
          }}
        >
          Unmask the blockchain,
          <br />
          unlock{" "}
          <span
            style={{
              color: "#7c3aed",
              display: "inline-block",
              border: "2.5px solid #a78bfa",
              borderRadius: "999px",
              padding: "0 18px 4px",
              fontWeight: 400,
              lineHeight: 1.15,
            }}
          >
            intelligence
          </span>
        </h1>

        <p
          style={{
            fontSize: "clamp(0.9rem, 1.6vw, 1.08rem)",
            color: "#3a3a5c",
            lineHeight: 1.75,
            maxWidth: 780,
            margin: 0,
          }}
        >
          Elementus labs is a pioneering blockchain intelligence platform focused on
          the flow of digital assets across an evolving Web3 ecosystem. With
          over 650 million addresses attributed to entities and identities across
          multiple chains, Elementus labs offers the transparency &amp; intelligence
          needed for actionable insights on the blockchain.
        </p>
      </section>

      {/* Bottom section: network visualization + CTA card */}
      <section
        style={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: "clamp(360px, 50vw, 520px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Network canvas — absolute fill */}
        <div style={{ position: "absolute", inset: 0 }}>
          <BlockchainNetwork />
        </div>

        {/* CTA card — responsive positioning */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            margin: "auto clamp(16px, 5vw, 64px) clamp(24px, 4vw, 48px)",
            alignSelf: "flex-start",
            background: "#13121f",
            borderRadius: "0 54px 54px 0px",
            padding: "clamp(24px, 4vw, 40px) clamp(24px, 4vw, 48px)",
            width: "min(460px, calc(100% - clamp(32px, 10vw, 128px)))",
            boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
            boxSizing: "border-box",
          }}
        >
          <h2
            style={{
              fontSize: "clamp(1.4rem, 3.5vw, 2.6rem)",
              fontWeight: 600,
              color: "#ffffff",
              lineHeight: 1.2,
              margin: "0 0 clamp(20px, 3vw, 32px)",
              letterSpacing: "-0.01em",
            }}
          >
            Blockchain
            <br />
            intelligence is at
            <br />
            your fingertips.
          </h2>

          <button
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "#7c3aed",
              color: "#fff",
              border: "none",
              borderRadius: 999,
              padding: "14px 28px",
              fontSize: "clamp(0.875rem, 1.5vw, 1rem)",
              fontWeight: 600,
              cursor: "pointer",
              width: "100%",
              justifyContent: "space-between",
              letterSpacing: "0.01em",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "#6d28d9")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "#7c3aed")
            }
          >
            <Link to="/login">
              Login
            </Link>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>
    </div>
  );
}